import React, { useContext, useState } from 'react';
import CustomDialog from '../../custom-dialog/custom-dialog';
import {
  DialogTitle,
  DialogContent,
  Button,
  CircularProgress,
  DialogActions,
  Stack,
  Typography,
} from '@mui/material';
import { LinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import CopyLinkButton from '../../copy-link-button/copy-link-button';
import { OrderIdWithPopover } from '../../order-id-with-popover/order-id-with-popover';
import CustomerWithNavigate from '../../customer-with-navigate/customer-with-navigate';
import GenericTable from '../../generic-table/generic-table';
import OrderNotes from '../../order-notes/order-notes';
import { IconWithText } from '../../styles/components';
import { showDeliveryState } from '../../styles/order-states.style';
import { showWorkflow } from '../../styles/workflow-display';
import { AlertsData, AlertType } from './alerts';
import { useSnackBar } from '../../../../contexts/snackbar';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { categorizeOrderAlerts, formatDaysLate } from './utils';
import { NotificationsPaused } from '@mui/icons-material';
import IncompleteAddressError from '../../incomplete-address-error/incomplete-address-error';

interface OrdersDialog {
  onClose: () => void;
  selectedType: AlertType.UNDELIVERED | AlertType.UNPAID;
  alertsData?: AlertsData;
}
function OrdersDialog({ onClose, selectedType, alertsData }: OrdersDialog) {
  const [reminderFor, setReminderFor] = useState<OrderType | null>(null);
  const { showMessage } = useSnackBar();
  const [loadingSaveReminder, setLoadingSaveReminder] =
    useState<boolean>(false);

  const { config } = useContext(AuthContext);

  const { active_orders, snoozed_orders } = categorizeOrderAlerts(alertsData);

  const snoozeAlert = async (order: OrderType, days: number) => {
    const endpoint = `${API_ENDPOINT}/api/v1/order/${order.id}/snooze/`;
    setLoadingSaveReminder(true);
    try {
      await axios.patch(endpoint, { days: days }, config);
      showMessage('Reminder Set Successfully');
      setReminderFor(null);
    } catch {
      showMessage('Failed to set reminder', 'error');
    }
    setLoadingSaveReminder(false);
  };

  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {selectedType === AlertType.UNDELIVERED
          ? 'Late Orders'
          : 'Unpaid Orders'}
      </DialogTitle>
      <DialogContent>
        <GenericTable<OrderType>
          sourceView="alerts-orders-dialog"
          items={
            selectedType === AlertType.UNDELIVERED
              ? [...active_orders.UNDELIVERED, ...snoozed_orders.UNDELIVERED]
              : [...active_orders.UNPAID, ...snoozed_orders.UNPAID]
          }
          selectedItems={new Map()}
          isLoading={false}
          fullWidth
          columns={[
            {
              header: (
                <>
                  <b>
                    Order Id <br /> (Ref #)
                  </b>
                </>
              ),
              id: 'order_id',
              display_body: (order) => (
                <OrderIdWithPopover order={order} />
              ),
            },
            {
              header: <b>Creation Date</b>,
              display_body: (order) => (
                <>
                  {order.creation_date == undefined
                    ? 'Unknown'
                    : format(order.creation_date!, 'dd-MM-yyyy HH:mm')}
                </>
              ),
              id: 'creation_date',
            },
            {
              id: 'customer',
              header: <b>Customer</b>,
              display_body: (order) => (
                <CustomerWithNavigate customer={order.customer} />
              ),
            },
            {
              header: <b>Merchant</b>,
              id: 'merchant',
              display_body: (order) => (
                <a href={`/merchant/${order.merchant?.id}`}>
                  <b>{order.merchant?.name}</b>
                </a>
              ),
            },
            {
              header: <b>Workflow</b>,
              id: 'workflow',
              display_body: (order) =>
                showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason),
            },
            {
          header: <b>Notes</b>,
          id: 'notes',
          display_body: (order) => <>
            {!order.customer_location?.area && <IncompleteAddressError /> }
            <OrderNotes order={order} />
          </>,
        },
            {
              header: <b>Status</b>,
              id: 'status',
              display_body: (order) => showDeliveryState(order.delivery_state),
            },
            {
              header: <b>Delay</b>,
              id: 'alert_explanation',
              display_body: (order) => (
                <b>
                  {formatDaysLate(
                    selectedType === AlertType.UNDELIVERED
                      ? order.creation_date ?? ''
                      : order.delivered_at ?? ''
                  )}
                </b>
              ),
            },
            {
              header: <b>Set Reminder</b>,
              id: 'reminder',
              display_body: (order) => {
                if (
                  order.snoozed_until &&
                  new Date(order.snoozed_until).getTime() > Date.now()
                ) {
                  return (
                    <IconWithText>
                      <NotificationsPaused color={'primary'} /> Muted until{' '}
                      {format(order.snoozed_until, 'dd-MM-yyyy')}
                    </IconWithText>
                  );
                }

                return (
                  <Button
                    onClick={() => setReminderFor(order)}
                    variant="contained"
                  >
                    {' '}
                    Set Reminder
                  </Button>
                );
              },
            },
          ]}
          getItemUID={(item) => item.id}
        />
      </DialogContent>
      {reminderFor && (
        <CustomDialog
          open={reminderFor != null}
          onClose={() => setReminderFor(null)}
          maxWidth="md"
        >
          <DialogTitle>
            Remind Me After{' '}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Order #{reminderFor.order_id}
            </Typography>
          </DialogTitle>

          <DialogContent>
            {/* Suggestion Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                disabled={loadingSaveReminder}
                onClick={() => snoozeAlert(reminderFor, 1)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                24 hours
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingSaveReminder}
                onClick={() => snoozeAlert(reminderFor, 2)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                48 hours
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingSaveReminder}
                onClick={() => snoozeAlert(reminderFor, 3)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                3 days
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingSaveReminder}
                onClick={() => snoozeAlert(reminderFor, 7)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                1 week
              </Button>
            </Stack>
            {loadingSaveReminder && <CircularProgress sx={{ mt: 2 }} />}
            <br />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReminderFor(null)}>Cancel</Button>
          </DialogActions>
        </CustomDialog>
      )}
    </CustomDialog>
  );
}

export default OrdersDialog;
